/**
 * Family Pet Guard (§24) — parent view.
 *
 * Add pets to the family, manage each pet's safe zones + QR finder tag,
 * toggle Lost Pet Mode (which lights up a public finder page), and see
 * finder scans/messages. Positioning rule: pets expand Kidzio, child
 * safety stays the core — this lives alongside, never above, the kids.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PawPrint, Plus, Trash2, QrCode, Siren, MapPin, MessageSquare, Camera, ScanLine } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { petsApi, type Pet, type Species } from '@/api/pets';
import { PageHeader } from './PageHeader';
import { EmptyState } from './EmptyState';

const SPECIES_ICON: Record<Species, string> = { dog: '🐕', cat: '🐈', other: '🐾' };

export function PetsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pets = useQuery({ queryKey: ['pets'], queryFn: () => petsApi.list() });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pets'] });

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.pets.title')}
        icon={PawPrint}
        tint="amber"
        description={t('pages.pets.description')}
      />
      <PetScanAlerts />
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_22rem]">
        <section className="space-y-4">
          {(pets.data ?? []).length === 0 && (
            <Card>
              <CardContent className="p-0">
                <EmptyState
                  icon={PawPrint}
                  tint="amber"
                  title={t('pages.pets.empty_title')}
                  description={t('pages.pets.empty_description')}
                />
              </CardContent>
            </Card>
          )}
          {(pets.data ?? []).map((pet) => (
            <PetCard key={pet.id} pet={pet} onChange={invalidate} />
          ))}
        </section>
        <aside><AddPetCard onAdded={invalidate} /></aside>
      </div>
    </div>
  );
}

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

function PetCard({ pet, onChange }: { pet: Pet; onChange: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tag = useQuery({ queryKey: ['pets', 'tag', pet.id], queryFn: () => petsApi.tag(pet.id) });
  const zones = useQuery({ queryKey: ['pets', 'zones', pet.id], queryFn: () => petsApi.zones(pet.id) });
  // Same query key as the unread banner — react-query dedupes the fetch
  // so this is essentially free.
  const scansQuery = useQuery({
    queryKey: ['pets', 'scans'],
    queryFn: () => petsApi.scans(),
    staleTime: 30_000,
  });
  const myScans = (scansQuery.data ?? [])
    .filter((s) => s.pet_id === pet.id)
    .slice(0, 6);
  const [showTag, setShowTag] = useState(false);

  const lost = useMutation({
    mutationFn: (on: boolean) => (on ? petsApi.activateLost(pet.id, t('pages.pets.lost_default_message')) : petsApi.deactivateLost(pet.id)),
    onSuccess: () => { toast.success(t('pages.pets.toast_updated')); onChange(); },
  });
  const remove = useMutation({ mutationFn: () => petsApi.remove(pet.id), onSuccess: onChange });
  const addZone = useMutation({
    mutationFn: () => petsApi.addZone(pet.id, { name: 'Home', center_lat: 40.188, center_lng: 44.516, radius_m: 150 }),
    onSuccess: () => { toast.success(t('pages.pets.toast_zone_added')); void queryClient.invalidateQueries({ queryKey: ['pets', 'zones', pet.id] }); },
  });
  const uploadPhoto = useMutation({
    mutationFn: (f: File) => petsApi.uploadPhoto(pet.id, f),
    onSuccess: () => { toast.success(t('pages.pets.toast_photo_updated', { name: pet.name })); onChange(); },
    onError: (e: Error) => toast.error(e.message.includes('photo_too_large') ? t('pages.pets.toast_photo_too_large') : t('pages.pets.toast_upload_failed')),
  });
  const deletePhoto = useMutation({
    mutationFn: () => petsApi.deletePhoto(pet.id),
    onSuccess: () => { toast.success(t('pages.pets.toast_photo_removed')); onChange(); },
  });

  const photoSrc = pet.photo_url ? `${API_BASE}${pet.photo_url}` : null;

  return (
    <Card className={pet.lost_active ? 'border-rose-300 bg-rose-50/40' : ''}>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <label
          className="group relative grid size-12 cursor-pointer place-items-center rounded-full bg-[color:var(--kz-color-surface-sunken)] text-2xl ring-1 ring-[color:var(--kz-color-border-subtle)]/60"
          title={photoSrc ? t('pages.pets.photo_replace') : t('pages.pets.photo_add')}
        >
          <span className="absolute inset-0 overflow-hidden rounded-full">
            {photoSrc ? (
              <img src={photoSrc} alt={pet.name} className="size-full object-cover" />
            ) : (
              <span className="grid size-full place-items-center">
                {SPECIES_ICON[pet.species]}
              </span>
            )}
          </span>
          {/* Always-visible camera badge — makes the avatar discoverable
              as an upload control on both desktop and touch. */}
          <span
            className="absolute -bottom-0.5 -right-0.5 grid size-5 place-items-center rounded-full bg-[color:var(--kz-color-brand-primary)] text-white ring-2 ring-[color:var(--kz-color-surface-card)] shadow-sm"
            aria-hidden
          >
            <Camera className="size-3" />
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadPhoto.mutate(f);
              e.target.value = ''; // allow re-picking the same file
            }}
          />
          {/* Hover overlay for desktop confirmation. */}
          <span className="absolute inset-0 hidden items-center justify-center rounded-full bg-black/45 text-[10px] font-bold uppercase tracking-wider text-white group-hover:flex">
            {uploadPhoto.isPending ? '…' : photoSrc ? t('pages.pets.photo_overlay_replace') : t('pages.pets.photo_overlay_add')}
          </span>
        </label>
        <div className="flex-1 min-w-0">
          <CardTitle className="flex items-center gap-2 text-base">
            {pet.name}
            {pet.lost_active && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800">{t('pages.pets.lost_badge')}</span>}
          </CardTitle>
          <CardDescription>
            {[pet.breed, pet.color, pet.age_years ? `${pet.age_years}y` : null].filter(Boolean).join(' · ') || pet.species}
          </CardDescription>
        </div>
        <Button size="sm" variant={pet.lost_active ? 'default' : 'outline'} onClick={() => lost.mutate(!pet.lost_active)} disabled={lost.isPending}>
          <Siren className="mr-1 size-3.5" />{pet.lost_active ? t('pages.pets.btn_found') : t('pages.pets.btn_lost')}
        </Button>
        <Button variant="ghost" size="icon" onClick={() => remove.mutate()} aria-label={t('pages.pets.remove_aria')}><Trash2 className="size-4" /></Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--kz-color-ink-muted)]">
          {pet.microchip_id && <span className="rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5">{t('pages.pets.chip_id', { id: pet.microchip_id })}</span>}
          <span className="inline-flex items-center gap-1"><MapPin className="size-3" /> {t('pages.pets.safe_zones_count', { count: (zones.data ?? []).length })}</span>
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => addZone.mutate()} disabled={addZone.isPending}>{t('pages.pets.add_home_zone')}</Button>
          {photoSrc && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-rose-700 hover:text-rose-900"
              onClick={() => deletePhoto.mutate()}
              disabled={deletePhoto.isPending}
            >
              {t('pages.pets.remove_photo')}
            </Button>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowTag((s) => !s)}>
          <QrCode className="mr-1 size-3.5" /> {showTag ? t('pages.pets.hide_qr') : t('pages.pets.show_qr')}
        </Button>
        {showTag && tag.data && (
          <div className="kz-qr-plate grid place-items-center rounded-card border border-[color:var(--kz-color-border-subtle)] bg-white p-3">
            <img src={petsApi.qrUrl(tag.data.token)} alt={t('pages.pets.qr_alt')} className="size-36" />
            <p className="mt-2 break-all text-center font-mono text-[11px] text-[color:var(--kz-color-ink-muted)]">{tag.data.url}</p>
          </div>
        )}

        {/* Recent scans history — mirrors the Safe ID `Recent scans` panel.
            Up to 6 most recent rows; map link when the finder shared coords. */}
        <div className="border-t border-[color:var(--kz-color-border-subtle)] pt-3">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <ScanLine className="size-4" /> {t('pages.pets.recent_scans')}
          </p>
          {myScans.length === 0 ? (
            <p className="mt-1 text-xs text-[color:var(--kz-color-ink-muted)]">
              {t('pages.pets.no_scans')}
            </p>
          ) : (
            <ul className="mt-2 space-y-1">
              {myScans.map((s) => {
                const hasCoords = s.approx_lat != null && s.approx_lng != null;
                const mapHref = hasCoords
                  ? `/map?lat=${s.approx_lat}&lng=${s.approx_lng}&pin_title=${encodeURIComponent(pet.name)}&pin_label=${encodeURIComponent(pet.name.slice(0, 2).toUpperCase())}&pin_color=%23f59e0b${pet.photo_url ? `&pin_photo=${encodeURIComponent(pet.photo_url.startsWith('/') ? `${API_BASE}${pet.photo_url}` : pet.photo_url)}` : ''}`
                  : null;
                return (
                  <li
                    key={s.id}
                    className="rounded-md border border-[color:var(--kz-color-border-subtle)]/40 bg-[color:var(--kz-color-surface-card)] px-2.5 py-1.5 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-1.5 text-[color:var(--kz-color-ink-secondary)]">
                      <span>{new Date(s.scanned_at).toLocaleString()}</span>
                      {!s.read_at && (
                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                          {t('pages.pets.new_chip')}
                        </span>
                      )}
                      {mapHref && (
                        <>
                          <span className="opacity-60">·</span>
                          <Link
                            to={mapHref}
                            className="inline-flex items-center gap-1 font-medium text-amber-800 underline-offset-2 hover:underline"
                          >
                            <MapPin className="size-3" />
                            {t('pages.pets.view_on_map')}
                          </Link>
                        </>
                      )}
                    </div>
                    {s.message && (
                      <p className="mt-1 italic text-[color:var(--kz-color-ink-primary)]">
                        &ldquo;{s.message}&rdquo;
                      </p>
                    )}
                    {s.finder_contact && (
                      <p className="mt-0.5 flex items-center gap-1 text-[color:var(--kz-color-ink-muted)]">
                        <span>{t('pages.pets.from')}</span>
                        <a
                          href={`tel:${s.finder_contact.replace(/[^+\d]/g, '')}`}
                          className="font-mono font-medium text-amber-800 underline-offset-2 hover:underline"
                        >
                          {s.finder_contact}
                        </a>
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AddPetCard({ onAdded }: { onAdded: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [species, setSpecies] = useState<Species>('dog');
  const [breed, setBreed] = useState('');
  const create = useMutation({
    mutationFn: () => petsApi.create({ name: name.trim(), species, breed: breed.trim() || undefined }),
    onSuccess: () => { toast.success(t('pages.pets.toast_pet_added')); setName(''); setBreed(''); onAdded(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : t('pages.pets.toast_add_failed')),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><PawPrint className="size-4" /> {t('pages.pets.add_title')}</CardTitle>
        <CardDescription>{t('pages.pets.add_description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-1.5">
          <Label htmlFor="p-name">{t('pages.pets.form_name')}</Label>
          <Input id="p-name" placeholder={t('pages.pets.form_name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="p-species">{t('pages.pets.form_species')}</Label>
            <select id="p-species" className="flex h-9 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent px-3 text-sm" value={species} onChange={(e) => setSpecies(e.target.value as Species)}>
              <option value="dog">{t('pages.pets.species_dog')}</option>
              <option value="cat">{t('pages.pets.species_cat')}</option>
              <option value="other">{t('pages.pets.species_other')}</option>
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="p-breed">{t('pages.pets.form_breed')}</Label>
            <Input id="p-breed" placeholder={t('pages.pets.form_breed_placeholder')} value={breed} onChange={(e) => setBreed(e.target.value)} />
          </div>
        </div>
        <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
          <Plus className="mr-1 size-4" /> {t('pages.pets.add_pet')}
        </Button>
      </CardContent>
    </Card>
  );
}

function PetScanAlerts() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scans = useQuery({ queryKey: ['pets', 'scans'], queryFn: () => petsApi.scans(), refetchInterval: 30_000 });
  const pets = useQuery({ queryKey: ['pets', 'list'], queryFn: () => petsApi.list(), staleTime: 60_000 });
  const read = useMutation({
    mutationFn: (id: string) => petsApi.markScanRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pets', 'scans'] }),
  });
  // Show every unread scan, including ones without a finder message — a
  // silent scan still means somebody touched the tag.
  const unread = (scans.data ?? []).filter((s) => !s.read_at);
  if (unread.length === 0) return null;
  return (
    <Card className="mt-6 border-amber-200 bg-amber-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-amber-900">
          <MessageSquare className="size-4" /> {t('pages.pets.scan_alert_title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {unread.map((s) => {
            const pet = pets.data?.find((p) => p.id === s.pet_id);
            const name = pet?.name ?? 'Pet';
            const hasCoords = s.approx_lat != null && s.approx_lng != null;
            // Build the map deep-link: include pin_photo when the pet has
            // an uploaded photo so the map pin renders it instead of the
            // colored initial circle.
            let mapHref: string | null = null;
            if (hasCoords) {
              const params = new URLSearchParams({
                lat: String(s.approx_lat),
                lng: String(s.approx_lng),
                pin_label: name.slice(0, 2).toUpperCase(),
                pin_title: name,
                pin_color: '#f59e0b',
              });
              if (pet?.photo_url) {
                const abs = pet.photo_url.startsWith('/')
                  ? `${API_BASE}${pet.photo_url}`
                  : pet.photo_url;
                params.set('pin_photo', abs);
              }
              mapHref = `/map?${params.toString()}`;
            }
            return (
              <li key={s.id} className="flex items-start gap-3 rounded-card bg-white p-3 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-amber-900">{name}</p>
                  {s.message ? (
                    <p className="mt-0.5 italic text-[color:var(--kz-color-ink-secondary)]">&ldquo;{s.message}&rdquo;</p>
                  ) : (
                    <p className="mt-0.5 text-xs text-[color:var(--kz-color-ink-muted)]">
                      {t('pages.pets.scan_no_message')}
                    </p>
                  )}
                  <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[color:var(--kz-color-ink-muted)]">
                    {s.finder_contact && (
                      <>
                        <span>{t('pages.pets.scan_contact')}</span>
                        <a
                          href={`tel:${s.finder_contact.replace(/[^+\d]/g, '')}`}
                          className="font-mono font-medium text-amber-800 underline-offset-2 hover:underline"
                        >
                          {s.finder_contact}
                        </a>
                        <span className="opacity-60">·</span>
                      </>
                    )}
                    <span>{new Date(s.scanned_at).toLocaleString()}</span>
                    {mapHref && (
                      <>
                        <span className="opacity-60">·</span>
                        <Link
                          to={mapHref}
                          className="inline-flex items-center gap-1 font-medium text-amber-800 underline-offset-2 hover:underline"
                        >
                          <MapPin className="size-3" />
                          {t('pages.pets.view_on_map')}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => read.mutate(s.id)}>{t('pages.pets.mark_read')}</Button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
